import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import systemCaregiverService from '../../services/systemCaregiverService';

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function getNameForSort(caregiver) {
  return `${caregiver?.first_name || ''} ${caregiver?.last_name || ''}`.trim().toLowerCase();
}

function sortSystemCaregiversList(list = [], sortBy = 'status', sortOrder = 'asc') {
  const statusOrder = { active: 1, inactive: 2 };
  const safeList = Array.isArray(list) ? [...list] : [];

  return safeList.sort((a, b) => {
    if (sortBy === 'status') {
      const orderA = statusOrder[normalizeStatus(a?.status)] || 99;
      const orderB = statusOrder[normalizeStatus(b?.status)] || 99;
      if (orderA !== orderB) {
        return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
      }
      return getNameForSort(a).localeCompare(getNameForSort(b));
    }

    if (sortBy === 'name' || sortBy === 'first_name') {
      const nameA = getNameForSort(a);
      const nameB = getNameForSort(b);
      return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }

    const valueA = a?.[sortBy] ?? '';
    const valueB = b?.[sortBy] ?? '';
    return sortOrder === 'asc'
      ? String(valueA).localeCompare(String(valueB), undefined, { numeric: true })
      : String(valueB).localeCompare(String(valueA), undefined, { numeric: true });
  });
}

export function buildSystemCaregiverCacheKey(params = {}) {
  const p = Number(params.page) || 1;
  const l = Number(params.limit) || 30;
  const s = String(params.search || '').trim().toLowerCase();
  const pr = params.practiceId != null && params.practiceId !== '' ? String(params.practiceId) : 'g';
  const sb = params.sortBy || 'status';
  const so = params.sortOrder || 'asc';
  return `${pr}_${p}_${l}_${s}_${sb}_${so}`;
}

// Async thunks
export const createSystemCaregiver = createAsyncThunk(
  'systemCaregiver/createSystemCaregiver',
  async (systemCaregiverData, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.createSystemCaregiver(systemCaregiverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create system caregiver');
    }
  }
);

export const fetchSystemCaregivers = createAsyncThunk(
  'systemCaregiver/fetchSystemCaregivers',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.getSystemCaregivers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch system caregivers');
    }
  },
  {
    condition: (params, { getState }) => {
      if (params?.force) return true;
      const st = getState().systemCaregiver;
      const key = buildSystemCaregiverCacheKey(params);
      return !st.systemCaregiverFetchedKeys[key];
    },
  }
);

export const updateSystemCaregiver = createAsyncThunk(
  'systemCaregiver/updateSystemCaregiver',
  async ({ systemCaregiverId, systemCaregiverData }, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.updateSystemCaregiver(systemCaregiverId, systemCaregiverData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update system caregiver');
    }
  }
);

export const deleteSystemCaregiver = createAsyncThunk(
  'systemCaregiver/deleteSystemCaregiver',
  async (systemCaregiverId, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.deleteSystemCaregiver(systemCaregiverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete system caregiver');
    }
  }
);

export const fetchSystemCaregiverById = createAsyncThunk(
  'systemCaregiver/fetchSystemCaregiverById',
  async (caregiverId, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.getSystemCaregiverById(caregiverId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch system caregiver');
    }
  }
);

export const fetchSystemCaregiverPatients = createAsyncThunk(
  'systemCaregiver/fetchSystemCaregiverPatients',
  async ({ caregiverId, params = {} }, { rejectWithValue }) => {
    try {
      const response = await systemCaregiverService.getSystemCaregiverPatients(caregiverId, params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch patients');
    }
  }
);

const initialState = {
  systemCaregivers: [],
  systemCaregiverListByKey: {},
  systemCaregiverPaginationByKey: {},
  systemCaregiverFetchedKeys: {},
  listFetchLoading: false,
  currentSystemCaregiver: null,
  patients: [],
  patientsPagination: {
    current_page: 1,
    per_page: 30,
    total: 0,
    total_pages: 1
  },
  isLoading: false,
  patientsLoading: false,
  error: null,
  pagination: {
    current_page: 1,
    per_page: 30,
    total: 0,
    total_pages: 0
  },
  sortBy: 'status',
  sortOrder: 'asc'
};

const systemCaregiverSlice = createSlice({
  name: 'systemCaregiver',
  initialState,
  reducers: {
    setSystemCaregivers: (state, action) => {
      state.systemCaregivers = action.payload;
    },
    setCurrentSystemCaregiver: (state, action) => {
      state.currentSystemCaregiver = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    hydrateSystemCaregiverList: (state, action) => {
      const key = action.payload;
      if (state.systemCaregiverFetchedKeys[key] && state.systemCaregiverListByKey[key]) {
        state.systemCaregivers = state.systemCaregiverListByKey[key];
        state.pagination = { ...state.pagination, ...state.systemCaregiverPaginationByKey[key] };
      }
    },
    invalidateSystemCaregiverListCache: (state) => {
      state.systemCaregiverFetchedKeys = {};
      state.systemCaregiverListByKey = {};
      state.systemCaregiverPaginationByKey = {};
    },
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
      // Invalidate cache when sorting changes if we want real-time backend sort
      state.systemCaregiverFetchedKeys = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Create system caregiver
      .addCase(createSystemCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSystemCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        const createdCaregiver = action.payload?.data;
        if (!createdCaregiver || createdCaregiver.deleted_at) return;

        const deduped = state.systemCaregivers.filter((caregiver) => caregiver.id !== createdCaregiver.id);
        state.systemCaregivers = sortSystemCaregiversList(
          [...deduped, createdCaregiver],
          state.sortBy,
          state.sortOrder
        );

        Object.keys(state.systemCaregiverListByKey).forEach((key) => {
          const existingList = state.systemCaregiverListByKey[key] || [];
          const withoutDuplicate = existingList.filter((caregiver) => caregiver.id !== createdCaregiver.id);
          state.systemCaregiverListByKey[key] = sortSystemCaregiversList(
            [...withoutDuplicate, createdCaregiver],
            state.sortBy,
            state.sortOrder
          );

        });
      })
      .addCase(createSystemCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch system caregivers
      .addCase(fetchSystemCaregivers.pending, (state) => {
        state.listFetchLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemCaregivers.fulfilled, (state, action) => {
        state.listFetchLoading = false;
        const caregivers = (action.payload.data.caregivers || []).filter(c => !c.deleted_at);
        const pag = action.payload.data.pagination || state.pagination;
        const key = buildSystemCaregiverCacheKey(action.meta.arg || {});
        state.systemCaregivers = caregivers;
        state.pagination = pag;
        state.systemCaregiverListByKey[key] = caregivers;
        state.systemCaregiverPaginationByKey[key] = pag;
        state.systemCaregiverFetchedKeys[key] = true;
      })
      .addCase(fetchSystemCaregivers.rejected, (state, action) => {
        state.listFetchLoading = false;
        state.error = action.payload;
      })
      // Update system caregiver
      .addCase(updateSystemCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSystemCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedCaregiver = action.payload?.data;
        if (!updatedCaregiver) return;

        const index = state.systemCaregivers.findIndex(sc => sc.id === updatedCaregiver.id);
        if (index !== -1) {
          state.systemCaregivers[index] = updatedCaregiver;
          state.systemCaregivers = sortSystemCaregiversList(state.systemCaregivers, state.sortBy, state.sortOrder);
        }

        Object.keys(state.systemCaregiverListByKey).forEach((key) => {
          const existingList = state.systemCaregiverListByKey[key] || [];
          const listIndex = existingList.findIndex((caregiver) => caregiver.id === updatedCaregiver.id);
          if (listIndex !== -1) {
            existingList[listIndex] = updatedCaregiver;
            state.systemCaregiverListByKey[key] = sortSystemCaregiversList(
              existingList,
              state.sortBy,
              state.sortOrder
            );
          }
        });
      })
      .addCase(updateSystemCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete system caregiver
      .addCase(deleteSystemCaregiver.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSystemCaregiver.fulfilled, (state, action) => {
        state.isLoading = false;
        const id = action.meta.arg;
        state.systemCaregivers = state.systemCaregivers.filter(sc => sc.id !== id);
        state.systemCaregiverFetchedKeys = {};
        state.systemCaregiverListByKey = {};
        state.systemCaregiverPaginationByKey = {};
      })
      .addCase(deleteSystemCaregiver.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch system caregiver by ID
      .addCase(fetchSystemCaregiverById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemCaregiverById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSystemCaregiver = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(fetchSystemCaregiverById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.currentSystemCaregiver = null;
      })
      // Fetch system caregiver patients
      .addCase(fetchSystemCaregiverPatients.pending, (state) => {
        state.patientsLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemCaregiverPatients.fulfilled, (state, action) => {
        state.patientsLoading = false;
        state.patients = action.payload.data || [];
        state.patientsPagination = action.payload.pagination || state.patientsPagination;
        state.error = null;
      })
      .addCase(fetchSystemCaregiverPatients.rejected, (state, action) => {
        state.patientsLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSystemCaregivers,
  setCurrentSystemCaregiver,
  setLoading,
  setError,
  clearError,
  hydrateSystemCaregiverList,
  invalidateSystemCaregiverListCache,
  setSorting,
} = systemCaregiverSlice.actions;

export default systemCaregiverSlice.reducer;
